import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { usePatients, useBulkDeletePatients, useDeletePatient } from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { joinFullName } from '../../utils/patientName';
import { composePatientAddress, permanentAddressParts } from '../../utils/patientAddress';

import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
  Users,
  Search,
  Plus,
  Loader2,
  AlertCircle,
  UserCircle,
  Phone,
  MapPin,
  Calendar,
  X,
  SlidersHorizontal,
  Eye,
  Edit3,
  Trash2,
} from 'lucide-react';

const genderOptions = [
  { value: '', label: 'All Genders' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const bloodGroupOptions = [
  { value: '', label: 'All Blood Groups' },
  { value: 'A+', label: 'A+' },
  { value: 'A-', label: 'A-' },
  { value: 'B+', label: 'B+' },
  { value: 'B-', label: 'B-' },
  { value: 'AB+', label: 'AB+' },
  { value: 'AB-', label: 'AB-' },
  { value: 'O+', label: 'O+' },
  { value: 'O-', label: 'O-' },
];

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

export function Patients() {
  const location = useLocation();
  const navigate = useNavigate();
  const limit = 20;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });

  const [searchQuery, setSearchQuery] = useState(() => params.get('search') || '');
  const [gender, setGender] = useState(() => params.get('gender') || '');
  const [bloodGroup, setBloodGroup] = useState(() => params.get('bloodGroup') || '');
  const [status, setStatus] = useState(() => params.get('status') || '');
  const [showFilters, setShowFilters] = useState(false);

  const debouncedSearch = useDebounce(searchQuery, 500);

  // Remembers the exact URL this component last wrote, so the URL -> state
  // sync below can ignore our own writes and only react to real navigation
  // (back/forward, manual URL edits). Without this, a keystroke that has not
  // yet been flushed to the URL can be clobbered by the previous URL value,
  // which drops characters while typing.
  const lastWrittenUrlRef = useRef<string | null>(null);

  const filters = useMemo(() => {
    const f: any = {};
    if (debouncedSearch.length >= 2) f.search = debouncedSearch;
    if (gender) f.gender = gender;
    if (bloodGroup) f.bloodGroup = bloodGroup;
    if (status) f.isActive = status === 'true';
    return f;
  }, [debouncedSearch, gender, bloodGroup, status]);

  const { data: patientsData, isLoading, error } = usePatients(page, limit, filters);
  const patients = useMemo(() => patientsData?.data ?? [], [patientsData]);
  const meta = patientsData?.meta;
  const trashedCount = Number(patientsData?.stats?.trashed ?? 0);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const bulkDeleteMutation = useBulkDeletePatients();
  const deleteMutation = useDeletePatient();

  const allSelected = patients.length > 0 && patients.every((p: any) => selectedIds.includes(p.id));
  const someSelected = patients.some((p: any) => selectedIds.includes(p.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  // Drop selections that are no longer visible (page or filter change).
  useEffect(() => {
    const visible = new Set(patients.map((p: any) => p.id));
    setSelectedIds((prev) => {
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [patients]);

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : patients.map((p: any) => p.id));
  };

  const toggleOne = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    try {
      const res = await bulkDeleteMutation.mutateAsync(ids);
      const deletedCount = res?.data?.deleted?.length ?? ids.length;
      toast.success(`Moved ${deletedCount} patient${deletedCount === 1 ? '' : 's'} to Trash`);
      setSelectedIds([]);
      setBulkConfirmOpen(false);
      // If the whole page was removed, step back so the user isn't left on a blank page.
      if (page > 1 && ids.length === patients.length) {
        setPage(page - 1);
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || 'Failed to delete selected patients. Nothing was deleted.',
      );
    }
  };

  const handleDeleteOne = async () => {
    if (!deleteTarget) return;
    const { id } = deleteTarget;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Patient moved to Trash');
      setSelectedIds((prev) => prev.filter((value) => value !== id));
      setDeleteTarget(null);
      // If the last row on a later page was removed, step back a page.
      if (page > 1 && patients.length === 1) {
        setPage(page - 1);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete patient.');
    }
  };

  // Sync state TO URL (debounced so we don't rewrite the URL on every keystroke)
  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    if (gender) newParams.set('gender', gender);
    if (bloodGroup) newParams.set('bloodGroup', bloodGroup);
    if (status) newParams.set('status', status);

    const query = newParams.toString();
    const newUrl = `${location.pathname}${query ? `?${query}` : ''}`;
    lastWrittenUrlRef.current = newUrl;
    if (newUrl !== `${location.pathname}${location.search}`) {
      navigate(newUrl, { replace: true });
    }
  }, [page, debouncedSearch, gender, bloodGroup, status, navigate, location.pathname, location.search]);

  // Sync state FROM URL on external changes (back button, manual URL edit)
  useEffect(() => {
    // Ignore URL changes we triggered ourselves; only react to real navigation.
    if (`${location.pathname}${location.search}` === lastWrittenUrlRef.current) return;

    const p = params.get('page');
    const urlPage = p ? Math.max(1, parseInt(p, 10)) : 1;
    const urlSearch = params.get('search') || '';
    const urlGender = params.get('gender') || '';
    const urlBloodGroup = params.get('bloodGroup') || '';
    const urlStatus = params.get('status') || '';

    if (urlPage !== page) setPage(urlPage);
    if (urlSearch !== searchQuery) setSearchQuery(urlSearch);
    if (urlGender !== gender) setGender(urlGender);
    if (urlBloodGroup !== bloodGroup) setBloodGroup(urlBloodGroup);
    if (urlStatus !== status) setStatus(urlStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const clearFilters = () => {
    setSearchQuery('');
    setGender('');
    setBloodGroup('');
    setStatus('');
    setPage(1);
    setSelectedIds([]);
  };

  const hasActiveFilters = searchQuery || gender || bloodGroup || status;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">Patients</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/patients/trash"
            className="px-4 py-2 border border-border text-text-muted hover:text-danger hover:border-danger rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
            title="View trash"
          >
            <Trash2 size={16} />
            Trash
            {trashedCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-semibold leading-none">
                {trashedCount > 99 ? '99+' : trashedCount}
              </span>
            )}
          </Link>
          <Link
            to="/patients/new"
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            Register Patient
          </Link>
        </div>
      </div>

      {/* Search & Filters Bar */}
      <div className="bg-surface rounded-xl border border-border p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search by name, mobile, or registration number..."
              className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {isLoading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-text-muted" />
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters((s) => !s)}
              className={`px-3 py-2.5 border rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap ${
                showFilters || hasActiveFilters
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-border text-text-muted hover:text-text'
              }`}
            >
              <SlidersHorizontal size={16} />
              Filters
              {hasActiveFilters && (
                <span className="ml-1 w-2 h-2 rounded-full bg-primary" />
              )}
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2.5 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-border">
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Gender</label>
              <select
                value={gender}
                onChange={(e) => { setGender(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              >
                {genderOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Blood Group</label>
              <select
                value={bloodGroup}
                onChange={(e) => { setBloodGroup(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              >
                {bloodGroupOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              >
                {statusOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Bulk selection bar */}
      {selectedIds.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteMutation.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load patients. Please try again.</span>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}

      {/* Patients Table */}
      {!isLoading && (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={patients.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Reg. #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Details</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Registered</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wider w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {patients.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-text-muted">
                      {hasActiveFilters ? (
                        <span>
                          No patients found matching your filters.{' '}
                          <button onClick={clearFilters} className="text-primary hover:underline">
                            Clear filters
                          </button>
                        </span>
                      ) : (
                        'No patients registered yet'
                      )}
                    </td>
                  </tr>
                ) : (
                  patients.map((patient: any) => (
                    <tr key={patient.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(patient.id)}
                          onChange={() => toggleOne(patient.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                          aria-label={`Select ${joinFullName(patient)}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <UserCircle size={16} className="text-primary" />
                          </div>
                          <div>
                            <Link
                              to={`/patients/${patient.id}`}
                              className="text-sm font-medium text-text hover:text-primary transition-colors"
                            >
                              {joinFullName(patient)}
                            </Link>
                            <p className="text-xs text-text-muted">{patient.gender || 'N/A'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-mono text-text">{patient.registrationNumber}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-text">
                          <Phone size={14} className="text-text-muted" />
                          {patient.mobileNumber}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-text-muted">
                          <MapPin size={14} />
                          {composePatientAddress(permanentAddressParts(patient)) || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-text-muted">
                          <Calendar size={14} />
                          {new Date(patient.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/patients/${patient.id}`}
                            className="p-1.5 text-text-muted hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </Link>
                          <Link
                            to={`/patients/${patient.id}/edit`}
                            className="p-1.5 text-text-muted hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                            title="Edit"
                          >
                            <Edit3 size={16} />
                          </Link>
                          <button
                            type="button"
                            onClick={() =>
                              setDeleteTarget({
                                id: patient.id,
                                name: joinFullName(patient),
                              })
                            }
                            className="p-1.5 text-text-muted hover:text-danger hover:bg-danger/10 rounded-md transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="patients"
          />
        </div>
      )}

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.length} patient${selectedIds.length === 1 ? '' : 's'}?`}
        description="This will move the selected patients to the Trash. You can restore them later."
        confirmLabel={`Delete ${selectedIds.length} patient${selectedIds.length === 1 ? '' : 's'}`}
        loading={bulkDeleteMutation.isPending}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title={`Delete ${deleteTarget?.name || 'patient'}?`}
        description="This will move the patient to the Trash. You can restore them later."
        confirmLabel="Delete patient"
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteOne}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
